import { join } from 'path';
import { writeFile, readFile, mkdir } from 'fs/promises';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { PairingConfigStore } from '@whittlelabs/shuttle';
import type { BrandConfig, SetupStepContext } from '@whittlelabs/shuttle';

import pkg from '../package.json';

/**
 * Bundle-injected build version (esbuild `define` in scripts/bundle.mjs). A
 * source / pre-release build carries a git-stamped `+dev.<sha>` suffix so it
 * reads as distinct in the Sift UI instead of masquerading as the last
 * published release; a `--release` build carries the bare package version.
 * Undefined under tsx dev / vitest (no bundle define), where we fall back to
 * package.json. `compareSemver` ignores build metadata, so gates are unaffected.
 */
declare const __SIFTER_BUILD_VERSION__: string | undefined;
const buildVersion =
  typeof __SIFTER_BUILD_VERSION__ !== 'undefined' ? __SIFTER_BUILD_VERSION__ : pkg.version;

/**
 * The whole Sifter whitelabel: pure BrandConfig data. All audit, spend,
 * and pairing logic lives in @whittlelabs/shuttle.
 *
 * Environment variables read at startup:
 *   KEEP_API_URL          - Keep API base URL
 *   JOBS_API_URL          - Jobs API base URL
 *   SIFTER_OAUTH_CLIENT_ID - OAuth client id provisioned in Keep's seed
 */
export const sifterBrand: BrandConfig = {
  product: {
    id: 'sifter',
    title: 'Whittle Sifter',
    cliBinary: 'whittle-sifter',
    packageName: '@whittlelabs/sifter',
    version: buildVersion,
    description:
      'Pairs with Whittle Sift to run AI code reviews on your hardware with your credentials.',
  },
  paths: {
    configDir: '~/.sifter',
    // After the post-pair setupStep below scaffolds it, run loads its
    // YAML from this path. The substrate fallback (~/.config/whittle-
    // labs/sifter.yaml) stays available for users who hand-write a
    // shared config across multiple machines.
    configSearchPaths: ['~/.sifter/sifter.yaml'],
  },
  executorAllowlist: ['claude-code', 'anthropic-api', 'http-api', 'webhook', 'custom-script'],
  // Overlay-fetch origins for the signed standards-content URLs each Sift
  // tier signs (SIFT_PUBLIC_API_URL). Without these, the executor's SSRF
  // guard silently skips the standards overlay and every review runs
  // standards-blind (2026-07-13). User config overrides per key.
  executorDefaults: {
    'claude-code': {
      overlayAllowedHosts: [
        'https://api.sift.whittlelabs.com',
        'https://api.sift.stage.whittlelabs.com',
        'http://localhost:3008',
      ],
    },
  },
  keepRegistration: {
    keepApiUrl: process.env.KEEP_API_URL ?? 'https://keep.whittlelabs.com',
    jobsApiUrl: process.env.JOBS_API_URL ?? 'https://jobs.whittlelabs.com',
    oauthClientId: process.env.SIFTER_OAUTH_CLIENT_ID ?? 'sifter',
    orgSlug: 'whittle-labs',
    roleSlug: 'sift-sifter',
    scopes: ['jobs:subscribe', 'jobs:submit'],
    serviceIdentityDisplayName: "{user}'s Sifter on {hostname}",
  },
  defaultCaps: {
    dailyTokens: 5_000_000,
    monthlyTokens: 100_000_000,
  },
  setupSteps: [
    {
      id: 'scaffold-run-config',
      phase: 'post-pair',
      // The substrate's run command requires a YAML describing pools,
      // executors, and Jobs auth. The web form that minted the pair command
      // bakes the user's product-level choices (executor type, poll interval)
      // into the init flags; this step reads them from `ctx.setupOptions`.
      //
      // A re-pair must refresh the pairing-derived fields (Jobs URL, service
      // identity, pools) even when sifter.yaml already exists — otherwise a
      // stale file silently shadows the new pairing (exactly how a leftover
      // `localhost:3005` once survived a fresh pair). So we merge rather than
      // skip: rebuild the pairing fields from `pairing`, carry over the user's
      // executor block (e.g. a claude-code `tmpDir`) and polling unless a fresh
      // flag overrides them, and log what happened. We only (re)scaffold an
      // executor block when there isn't one to preserve.
      run: async (ctx) => {
        const yamlPath = join(ctx.configDir, 'sifter.yaml');
        const pairing = new PairingConfigStore(join(ctx.configDir, 'config.json')).read();
        if (pairing.jobPools.length === 0) {
          throw new Error(
            'Pairing returned no job pools. The Sift backend should have provisioned one for this user during pre-pair.',
          );
        }

        let existing: Record<string, unknown> = {};
        try {
          existing =
            (parseYaml(await readFile(yamlPath, 'utf8')) as Record<string, unknown> | null) ?? {};
        } catch (err) {
          if ((err as NodeJS.ErrnoException).code !== 'ENOENT') throw err;
        }
        const isUpdate = Object.keys(existing).length > 0;

        const existingExecutors =
          (existing.executors as Record<string, unknown> | undefined) ?? {};
        const existingPools = Array.isArray(existing.pools)
          ? (existing.pools as Array<Record<string, unknown>>)
          : [];
        const priorExecutor =
          typeof existingPools[0]?.executor === 'string'
            ? (existingPools[0].executor as string)
            : undefined;

        // The user's --executor flag wins; else keep what the file used; else default.
        const executor = ctx.setupOptions.executor ?? priorExecutor ?? 'claude-code';
        const executorBlock: unknown =
          !ctx.setupOptions.executor && existingExecutors[executor] !== undefined
            ? existingExecutors[executor]
            : await buildExecutorBlock(executor, ctx);

        const doc: Record<string, unknown> = {
          auth: {
            jobsApiUrl: pairing.jobsApiUrl,
          },
          agent: {
            displayName: pairing.serviceIdentity.displayName,
            identityId: pairing.serviceIdentity.id,
          },
          pools: pairing.jobPools.map((p) => ({
            id: p.id,
            name: p.name,
            executor,
          })),
          executors: {
            ...existingExecutors,
            [executor]: executorBlock,
          },
        };

        const pollIntervalMs =
          ctx.setupOptions.pollIntervalMs ??
          (existing.polling as { intervalMs?: number } | undefined)?.intervalMs;
        if (pollIntervalMs !== undefined) {
          doc.polling = { intervalMs: pollIntervalMs };
        }

        await mkdir(ctx.configDir, { recursive: true });
        await writeFile(yamlPath, stringifyYaml(doc));
        ctx.log(
          isUpdate
            ? `Refreshed ${yamlPath} from this pairing (Jobs URL, identity, pools); kept your executor and polling settings.`
            : `Wrote ${yamlPath}.`,
        );
      },
    },
  ],
};

/**
 * Per-executor scaffold. New executor types are wired here; the
 * executor allowlist on the brand caps which `--executor=...` values
 * `init` will accept in the first place.
 */
async function buildExecutorBlock(
  executor: string,
  ctx: SetupStepContext,
): Promise<Record<string, unknown>> {
  switch (executor) {
    case 'claude-code': {
      // No working directory to ask for: every review runs in a fresh
      // throwaway checkout under the temp dir. `--tmp-dir` optionally moves
      // that root off the OS default (small/slow/wrong-volume temp); omit it
      // and the executor uses os.tmpdir(). Nothing to prompt for.
      return {
        type: 'claude-code',
        ...(ctx.setupOptions.tmpDir ? { tmpDir: ctx.setupOptions.tmpDir } : {}),
      };
    }
    case 'anthropic-api': {
      // The API key lives in `ANTHROPIC_API_KEY`, not in the YAML, so
      // the file is safe to share / commit / sync. The substrate's
      // env-ref resolver substitutes the value at executor startup;
      // an unset variable fails fast rather than producing 401s on
      // every claim. Users can hard-code a literal key here if they
      // really want to (e.g. in an air-gapped install), but the
      // env-var path is the default we document.
      return {
        type: 'anthropic-api',
        apiKey: '${ANTHROPIC_API_KEY}',
      };
    }
    default:
      // The substrate executor allowlist gates which strings make it
      // through. New executor types add a case here.
      return { type: executor };
  }
}

